import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'https://agents.matix.one';

const MaintenancePlans = () => {
  const { lang, t } = useLanguage();
  const f = t('fsm') || {};
  const isRTL = lang === 'ar';
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [clientEquipment, setClientEquipment] = useState([]);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({ status: '', client_id: '', search: '' });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  const [formData, setFormData] = useState({
    title: '', title_ar: '', description: '',
    client_id: '', contract_id: '',
    recurrence_type: 'monthly', recurrence_interval: 1, recurrence_day: '',
    priority: 'medium', estimated_duration_hours: '',
    checklist: [],
    instructions: '', instructions_ar: '',
    start_date: '', end_date: '',
    assigned_technician_id: '',
    auto_generate_wo: false, advance_days: 7,
    status: 'active',
    equipment_ids: []
  });

  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = React.useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredClients = clients.filter(c => {
    if (!clientSearch) return true;
    const s = clientSearch.toLowerCase();
    return (c.name || '').toLowerCase().includes(s) ||
           (c.name_ar || '').toLowerCase().includes(s) ||
           (c.phone || '').includes(s) ||
           (c.code || '').toLowerCase().includes(s);
  });

  const selectedClientName = (() => {
    if (!formData.client_id) return '';
    const c = clients.find(cl => String(cl.id) === String(formData.client_id));
    if (!c) return '';
    return isRTL ? (c.name_ar || c.name) : c.name;
  })();

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // ======================== STYLES ========================
  const inputSt = {
    width: '100%', padding: '12px',
    border: '2px solid rgba(255,255,255,0.2)',
    borderRadius: '8px', backgroundColor: '#1a2038',
    color: '#f9fafb', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box'
  };

  const labelSt = {
    display: 'block', marginBottom: '8px',
    color: '#d1d5db', fontSize: '13px', fontWeight: '500'
  };

  const sectionSt = {
    backgroundColor: 'rgba(26, 32, 56, 0.8)',
    padding: '25px', borderRadius: '12px',
    marginBottom: '20px',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  };

  const statCardSt = (color) => ({
    backgroundColor: 'rgba(26, 32, 56, 0.6)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px', padding: '20px',
    borderLeft: `4px solid ${color}`, flex: '1', minWidth: '160px'
  });

  const badgeSt = (bg, color) => ({
    display: 'inline-block', padding: '4px 12px',
    borderRadius: '20px', fontSize: '12px', fontWeight: '600',
    backgroundColor: bg, color: color
  });

  const btnPrimary = {
    backgroundColor: '#007bff', color: 'white', border: 'none',
    borderRadius: '10px', padding: '12px 24px',
    fontSize: '15px', fontWeight: '600', cursor: 'pointer'
  };

  const btnSecondary = {
    backgroundColor: 'transparent', color: '#d1d5db',
    border: '2px solid rgba(255,255,255,0.2)',
    borderRadius: '8px', padding: '10px 20px', cursor: 'pointer'
  };

  // ======================== DATA FETCHING ========================
  useEffect(() => { fetchPlans(); fetchClients(); }, [page, filters.status, filters.client_id]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ page, limit: 20 });
      if (filters.status) params.append('status', filters.status);
      if (filters.client_id) params.append('client_id', filters.client_id);

      const res = await fetch(`${API}/api/v1/fsm/maintenance-plans?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setPlans(data.data || []);
        setStats(data.stats || {});
        setPagination(data.pagination || {});
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/v1/fsm/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setClients(data.data || []);
    } catch (err) { console.error('Error fetching clients:', err); }
  };

  const fetchClientContracts = async (clientId) => {
    if (!clientId) { setContracts([]); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/v1/fsm/contracts?client_id=${clientId}&status=active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setContracts(data.data || []);
    } catch (err) { console.error('Error fetching contracts:', err); }
  };

  const fetchClientEquipment = async (clientId) => {
    if (!clientId) { setClientEquipment([]); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/v1/fsm/equipment?client_id=${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setClientEquipment(data.data || []);
    } catch (err) { console.error('Error fetching equipment:', err); }
  };

  // ======================== CRUD HANDLERS ========================
  const openCreateModal = () => {
    setEditingPlan(null);
    setFormData({
      title: '', title_ar: '', description: '',
      client_id: '', contract_id: '',
      recurrence_type: 'monthly', recurrence_interval: 1, recurrence_day: '',
      priority: 'medium', estimated_duration_hours: '',
      checklist: [],
      instructions: '', instructions_ar: '',
      start_date: '', end_date: '',
      assigned_technician_id: '',
      auto_generate_wo: false, advance_days: 7,
      status: 'active',
      equipment_ids: []
    });
    setContracts([]);
    setClientEquipment([]);
    setClientSearch('');
    setShowModal(true);
  };

  const openEditModal = async (plan) => {
    setEditingPlan(plan);
    // Fetch full plan with equipment
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/v1/fsm/maintenance-plans/${plan.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        const p = data.data;
        setFormData({
          title: p.title || '', title_ar: p.title_ar || '', description: p.description || '',
          client_id: p.client_id || '', contract_id: p.contract_id || '',
          recurrence_type: p.recurrence_type || 'monthly',
          recurrence_interval: p.recurrence_interval || 1,
          recurrence_day: p.recurrence_day || '',
          priority: p.priority || 'medium',
          estimated_duration_hours: p.estimated_duration_hours || '',
          checklist: p.checklist ? (typeof p.checklist === 'string' ? JSON.parse(p.checklist) : p.checklist) : [],
          instructions: p.instructions || '', instructions_ar: p.instructions_ar || '',
          start_date: p.start_date ? p.start_date.split('T')[0] : '',
          end_date: p.end_date ? p.end_date.split('T')[0] : '',
          assigned_technician_id: p.assigned_technician_id || '',
          auto_generate_wo: !!p.auto_generate_wo, advance_days: p.advance_days || 7,
          status: p.status || 'active',
          equipment_ids: (p.equipment || []).map(e => e.equipment_id)
        });
        if (p.client_id) {
          fetchClientContracts(p.client_id);
          fetchClientEquipment(p.client_id);
        }
        setClientSearch('');
      }
    } catch (err) { console.error('Error fetching plan:', err); }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.client_id || !formData.recurrence_type) {
      alert(f.fill_required_fields || 'Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingPlan
        ? `${API}/api/v1/fsm/maintenance-plans/${editingPlan.id}`
        : `${API}/api/v1/fsm/maintenance-plans`;

      const res = await fetch(url, {
        method: editingPlan ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        setShowModal(false);
        fetchPlans();
      } else {
        alert(data.message || 'Error saving plan');
      }
    } catch (err) {
      console.error('Error saving plan:', err);
      alert('Failed to save maintenance plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm(f.confirm_delete || 'Are you sure you want to delete this plan?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/v1/fsm/maintenance-plans/${planId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) fetchPlans();
    } catch (err) { console.error('Error deleting plan:', err); }
  };

  const handleClientChange = (clientId) => {
    handleChange('client_id', clientId);
    handleChange('contract_id', '');
    handleChange('equipment_ids', []);
    fetchClientContracts(clientId);
    fetchClientEquipment(clientId);
  };

  const toggleEquipment = (eqId) => {
    const current = formData.equipment_ids || [];
    if (current.includes(eqId)) {
      handleChange('equipment_ids', current.filter(id => id !== eqId));
    } else {
      handleChange('equipment_ids', [...current, eqId]);
    }
  };

  const addChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    handleChange('checklist', [...(formData.checklist || []), { text: newChecklistItem.trim(), required: false }]);
    setNewChecklistItem('');
  };

  const removeChecklistItem = (idx) => {
    const updated = [...(formData.checklist || [])];
    updated.splice(idx, 1);
    handleChange('checklist', updated);
  };

  // ======================== HELPERS ========================
  const getStatusBadge = (status) => {
    const map = {
      active:    { bg: 'rgba(22,163,106,0.2)', color: '#4ade80' },
      paused:    { bg: 'rgba(217,119,6,0.2)',   color: '#fbbf24' },
      completed: { bg: 'rgba(37,99,235,0.2)',   color: '#60a5fa' },
      cancelled: { bg: 'rgba(220,38,38,0.2)',   color: '#f87171' }
    };
    const s = map[status] || map.active;
    return badgeSt(s.bg, s.color);
  };

  const recurrenceLabel = (type, interval) => {
    const labels = {
      daily: f.daily || 'Daily',
      weekly: f.weekly || 'Weekly',
      monthly: f.monthly || 'Monthly',
      quarterly: f.quarterly || 'Quarterly',
      semi_annual: f.semi_annual || 'Semi-Annual',
      annual: f.annual || 'Annual',
      custom: f.custom || 'Custom'
    };
    const label = labels[type] || type;
    return interval > 1 ? `${f.every || 'Every'} ${interval} ${label}` : label;
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-GB'); }
    catch { return d; }
  };

  const priorityBadge = (p) => {
    const map = {
      low:      { bg: 'rgba(37,99,235,0.2)',   color: '#60a5fa' },
      medium:   { bg: 'rgba(217,119,6,0.2)',   color: '#fbbf24' },
      high:     { bg: 'rgba(234,88,12,0.2)',   color: '#fb923c' },
      critical: { bg: 'rgba(220,38,38,0.2)',   color: '#f87171' }
    };
    const s = map[p] || map.medium;
    return badgeSt(s.bg, s.color);
  };

  // ======================== RENDER ========================
  return (
    <Layout>
      <div style={{ direction: isRTL ? 'rtl' : 'ltr', padding: '24px', color: '#f9fafb' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>
              {f.maintenance_plans || 'Maintenance Plans'}
            </h1>
            <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: '14px' }}>
              {f.maintenance_plans_desc || 'Schedule recurring maintenance for equipment'}
            </p>
          </div>
          <button onClick={openCreateModal} style={btnPrimary}>
            + {f.new_plan || 'New Plan'}
          </button>
        </div>

        {/* Stat Cards */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <div style={statCardSt('#60a5fa')}>
            <div style={{ color: '#9ca3af', fontSize: '13px' }}>{f.total_plans || 'Total Plans'}</div>
            <div style={{ fontSize: '28px', fontWeight: '700', marginTop: '4px' }}>{stats.total_plans || 0}</div>
          </div>
          <div style={statCardSt('#4ade80')}>
            <div style={{ color: '#9ca3af', fontSize: '13px' }}>{f.active_plans || 'Active'}</div>
            <div style={{ fontSize: '28px', fontWeight: '700', marginTop: '4px', color: '#4ade80' }}>{stats.active_plans || 0}</div>
          </div>
          <div style={statCardSt('#fbbf24')}>
            <div style={{ color: '#9ca3af', fontSize: '13px' }}>{f.paused_plans || 'Paused'}</div>
            <div style={{ fontSize: '28px', fontWeight: '700', marginTop: '4px', color: '#fbbf24' }}>{stats.paused_plans || 0}</div>
          </div>
          <div style={statCardSt('#f87171')}>
            <div style={{ color: '#9ca3af', fontSize: '13px' }}>{f.overdue || 'Overdue'}</div>
            <div style={{ fontSize: '28px', fontWeight: '700', marginTop: '4px', color: '#f87171' }}>{stats.overdue_plans || 0}</div>
          </div>
          <div style={statCardSt('#c084fc')}>
            <div style={{ color: '#9ca3af', fontSize: '13px' }}>{f.due_this_week || 'Due This Week'}</div>
            <div style={{ fontSize: '28px', fontWeight: '700', marginTop: '4px', color: '#c084fc' }}>{stats.due_this_week || 0}</div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ ...sectionSt, display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <input type="text" placeholder={f.search_plans || 'Search plans...'}
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              style={inputSt} />
          </div>
          <div style={{ minWidth: '160px' }}>
            <select value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              style={inputSt}>
              <option value="">{f.all_statuses || 'All Statuses'}</option>
              <option value="active">{f.active || 'Active'}</option>
              <option value="paused">{f.paused || 'Paused'}</option>
              <option value="completed">{f.completed || 'Completed'}</option>
              <option value="cancelled">{f.cancelled || 'Cancelled'}</option>
            </select>
          </div>
          <div style={{ minWidth: '200px' }}>
            <select value={filters.client_id}
              onChange={(e) => setFilters(prev => ({ ...prev, client_id: e.target.value }))}
              style={inputSt}>
              <option value="">{f.all_clients || 'All Clients'}</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>
                  {isRTL ? (c.name_ar || c.name) : c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Plans Table */}
        <div style={{ ...sectionSt, padding: '0', overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
              {f.loading || 'Loading...'}
            </div>
          ) : plans.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
              {f.no_plans || 'No maintenance plans found'}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  {[
                    f.plan_title || 'Title',
                    f.client || 'Client',
                    f.recurrence || 'Recurrence',
                    f.priority || 'Priority',
                    f.next_due || 'Next Due',
                    f.equipment_count || 'Equipment',
                    f.status || 'Status',
                    f.actions || 'Actions'
                  ].map((h, i) => (
                    <th key={i} style={{ padding: '14px 16px', textAlign: isRTL ? 'right' : 'left', color: '#9ca3af', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {plans.filter(p => {
                  if (!filters.search) return true;
                  const s = filters.search.toLowerCase();
                  return (p.title || '').toLowerCase().includes(s) ||
                         (p.title_ar || '').toLowerCase().includes(s) ||
                         (p.client_name || '').toLowerCase().includes(s);
                }).map(plan => (
                  <tr key={plan.id}
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>
                        {isRTL ? (plan.title_ar || plan.title) : plan.title}
                      </div>
                      {plan.contract_title && (
                        <div style={{ color: '#9ca3af', fontSize: '12px', marginTop: '2px' }}>
                          {plan.contract_title}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '14px' }}>
                      {isRTL ? (plan.client_name_ar || plan.client_name) : plan.client_name}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '14px' }}>
                      {recurrenceLabel(plan.recurrence_type, plan.recurrence_interval)}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={priorityBadge(plan.priority)}>
                        {f[plan.priority] || plan.priority}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '14px' }}>
                      <span style={{
                        color: plan.next_due_date && new Date(plan.next_due_date) < new Date() ? '#f87171' : '#d1d5db'
                      }}>
                        {formatDate(plan.next_due_date)}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '14px', textAlign: 'center' }}>
                      {plan.equipment_count || 0}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={getStatusBadge(plan.status)}>
                        {f[plan.status] || plan.status}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={(e) => { e.stopPropagation(); openEditModal(plan); }}
                          style={{ ...btnSecondary, padding: '6px 12px', fontSize: '13px' }}>
                          {f.edit || 'Edit'}
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(plan.id); }}
                          style={{ ...btnSecondary, padding: '6px 12px', fontSize: '13px', borderColor: 'rgba(220,38,38,0.4)', color: '#f87171' }}>
                          {f.delete || 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
              <button key={p}
                onClick={() => setPage(p)}
                style={{
                  padding: '8px 14px', borderRadius: '8px', border: 'none',
                  backgroundColor: p === page ? '#007bff' : 'rgba(255,255,255,0.1)',
                  color: p === page ? 'white' : '#9ca3af',
                  cursor: 'pointer', fontSize: '14px'
                }}>
                {p}
              </button>
            ))}
          </div>
        )}

        {/* ======================== CREATE/EDIT MODAL ======================== */}
        {showModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex',
            justifyContent: 'center', alignItems: 'flex-start',
            zIndex: 1000, padding: '40px 20px', overflowY: 'auto'
          }}>
            <div style={{
              backgroundColor: '#151b30', borderRadius: '16px',
              width: '100%', maxWidth: '800px',
              border: '1px solid rgba(255,255,255,0.1)',
              maxHeight: '90vh', overflowY: 'auto'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.1)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                position: 'sticky', top: 0, backgroundColor: '#151b30', zIndex: 1
              }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>
                  {editingPlan
                    ? (f.edit_plan || 'Edit Maintenance Plan')
                    : (f.new_plan || 'New Maintenance Plan')}
                </h2>
                <button onClick={() => setShowModal(false)}
                  style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '24px', cursor: 'pointer' }}>
                  ×
                </button>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleSubmit} style={{ padding: '24px' }}>

                {/* Section: Basic Info */}
                <div style={sectionSt}>
                  <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '600', color: '#60a5fa' }}>
                    {f.basic_info || 'Basic Information'}
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={labelSt}>{f.title_en || 'Title (EN)'} *</label>
                      <input type="text" value={formData.title}
                        onChange={(e) => handleChange('title', e.target.value)}
                        style={inputSt} required />
                    </div>
                    <div>
                      <label style={labelSt}>{f.title_ar || 'Title (AR)'}</label>
                      <input type="text" value={formData.title_ar}
                        onChange={(e) => handleChange('title_ar', e.target.value)}
                        style={{ ...inputSt, direction: 'rtl' }} />
                    </div>
                  </div>
                  <div style={{ marginTop: '16px' }}>
                    <label style={labelSt}>{f.description || 'Description'}</label>
                    <textarea value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      style={{ ...inputSt, minHeight: '80px', resize: 'vertical' }}
                      rows={3} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                    <div>
                      <label style={labelSt}>{f.client || 'Client'} *</label>
                      <div ref={clientDropdownRef} style={{ position: 'relative' }}>
                        <input
                          type="text"
                          value={showClientDropdown ? clientSearch : selectedClientName}
                          placeholder={f.select_client || 'Select Client'}
                          onFocus={() => { setShowClientDropdown(true); setClientSearch(''); }}
                          onChange={(e) => { setClientSearch(e.target.value); setShowClientDropdown(true); }}
                          style={{ ...inputSt, cursor: 'pointer' }}
                          autoComplete="off"
                        />
                        {formData.client_id && !showClientDropdown && (
                          <span
                            onClick={() => { handleClientChange(''); setClientSearch(''); }}
                            style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)',
                              [isRTL ? 'left' : 'right']: '12px', color: '#9ca3af', cursor: 'pointer',
                              fontSize: '16px', lineHeight: 1 }}>×</span>
                        )}
                        {showClientDropdown && (
                          <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0,
                            backgroundColor: '#1e2746', border: '2px solid rgba(255,255,255,0.2)',
                            borderRadius: '8px', marginTop: '4px', maxHeight: '220px',
                            overflowY: 'auto', zIndex: 10,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
                          }}>
                            {filteredClients.length === 0 ? (
                              <div style={{ padding: '12px 16px', color: '#9ca3af', fontSize: '14px' }}>
                                {f.no_clients || 'No clients found'}
                              </div>
                            ) : filteredClients.map(c => (
                              <div key={c.id}
                                onClick={() => {
                                  handleClientChange(String(c.id));
                                  setShowClientDropdown(false);
                                  setClientSearch('');
                                }}
                                style={{
                                  padding: '10px 16px', cursor: 'pointer', fontSize: '14px',
                                  color: String(c.id) === String(formData.client_id) ? '#60a5fa' : '#f9fafb',
                                  backgroundColor: String(c.id) === String(formData.client_id)
                                    ? 'rgba(96,165,250,0.15)' : 'transparent',
                                  display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor =
                                  String(c.id) === String(formData.client_id) ? 'rgba(96,165,250,0.15)' : 'transparent'}
                              >
                                <span>{isRTL ? (c.name_ar || c.name) : c.name}</span>
                                {String(c.id) === String(formData.client_id) && <span>✓</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label style={labelSt}>{f.contract || 'Contract'}</label>
                      <select value={formData.contract_id}
                        onChange={(e) => handleChange('contract_id', e.target.value)}
                        style={inputSt}>
                        <option value="">{f.no_contract || 'No Contract'}</option>
                        {contracts.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.contract_number} - {c.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                    <div>
                      <label style={labelSt}>{f.priority || 'Priority'}</label>
                      <select value={formData.priority}
                        onChange={(e) => handleChange('priority', e.target.value)}
                        style={inputSt}>
                        <option value="low">{f.low || 'Low'}</option>
                        <option value="medium">{f.medium || 'Medium'}</option>
                        <option value="high">{f.high || 'High'}</option>
                        <option value="critical">{f.critical || 'Critical'}</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelSt}>{f.status || 'Status'}</label>
                      <select value={formData.status}
                        onChange={(e) => handleChange('status', e.target.value)}
                        style={inputSt}>
                        <option value="active">{f.active || 'Active'}</option>
                        <option value="paused">{f.paused || 'Paused'}</option>
                        <option value="completed">{f.completed || 'Completed'}</option>
                        <option value="cancelled">{f.cancelled || 'Cancelled'}</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Section: Recurrence Schedule */}
                <div style={sectionSt}>
                  <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '600', color: '#60a5fa' }}>
                    {f.recurrence_schedule || 'Recurrence Schedule'}
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={labelSt}>{f.recurrence_type || 'Recurrence Type'} *</label>
                      <select value={formData.recurrence_type}
                        onChange={(e) => handleChange('recurrence_type', e.target.value)}
                        style={inputSt} required>
                        <option value="daily">{f.daily || 'Daily'}</option>
                        <option value="weekly">{f.weekly || 'Weekly'}</option>
                        <option value="monthly">{f.monthly || 'Monthly'}</option>
                        <option value="quarterly">{f.quarterly || 'Quarterly'}</option>
                        <option value="semi_annual">{f.semi_annual || 'Semi-Annual'}</option>
                        <option value="annual">{f.annual || 'Annual'}</option>
                        <option value="custom">{f.custom || 'Custom'}</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelSt}>{f.interval || 'Interval'}</label>
                      <input type="text" inputMode="numeric" value={formData.recurrence_interval}
                        onChange={(e) => handleChange('recurrence_interval', e.target.value)}
                        style={inputSt} placeholder="1" />
                    </div>
                    <div>
                      <label style={labelSt}>{f.day_of_period || 'Day'}</label>
                      <input type="text" inputMode="numeric" value={formData.recurrence_day}
                        onChange={(e) => handleChange('recurrence_day', e.target.value)}
                        style={inputSt}
                        placeholder={formData.recurrence_type === 'weekly' ? '0-6' : '1-31'} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginTop: '16px' }}>
                    <div>
                      <label style={labelSt}>{f.start_date || 'Start Date'}</label>
                      <input type="date" value={formData.start_date}
                        onChange={(e) => handleChange('start_date', e.target.value)}
                        style={inputSt} />
                    </div>
                    <div>
                      <label style={labelSt}>{f.end_date || 'End Date'}</label>
                      <input type="date" value={formData.end_date}
                        onChange={(e) => handleChange('end_date', e.target.value)}
                        style={inputSt} />
                    </div>
                    <div>
                      <label style={labelSt}>{f.estimated_hours || 'Est. Duration (hrs)'}</label>
                      <input type="text" inputMode="numeric" value={formData.estimated_duration_hours}
                        onChange={(e) => handleChange('estimated_duration_hours', e.target.value)}
                        style={inputSt} />
                    </div>
                  </div>
                </div>

                {/* Section: Auto Work Order */}
                <div style={sectionSt}>
                  <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '600', color: '#60a5fa' }}>
                    {f.auto_work_order || 'Auto Work Order Generation'}
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <input type="checkbox" id="auto_wo"
                      checked={formData.auto_generate_wo}
                      onChange={(e) => handleChange('auto_generate_wo', e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: '#007bff' }} />
                    <label htmlFor="auto_wo" style={{ color: '#d1d5db', fontSize: '14px', cursor: 'pointer' }}>
                      {f.auto_generate_wo_label || 'Automatically generate work orders when due'}
                    </label>
                  </div>
                  {formData.auto_generate_wo && (
                    <div style={{ maxWidth: '300px' }}>
                      <label style={labelSt}>{f.advance_days || 'Generate WO (days before due)'}</label>
                      <input type="text" inputMode="numeric" value={formData.advance_days}
                        onChange={(e) => handleChange('advance_days', e.target.value)}
                        style={inputSt} />
                    </div>
                  )}
                </div>

                {/* Section: Equipment Selection */}
                {formData.client_id && (
                  <div style={sectionSt}>
                    <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '600', color: '#60a5fa' }}>
                      {f.equipment || 'Equipment'} ({(formData.equipment_ids || []).length} {f.selected || 'selected'})
                    </h3>
                    {clientEquipment.length === 0 ? (
                      <div style={{ color: '#9ca3af', fontSize: '14px' }}>
                        {f.no_equipment_for_client || 'No equipment found for this client'}
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '10px' }}>
                        {clientEquipment.map(eq => {
                          const isSelected = (formData.equipment_ids || []).includes(eq.id);
                          return (
                            <div key={eq.id}
                              onClick={() => toggleEquipment(eq.id)}
                              style={{
                                padding: '12px', borderRadius: '8px', cursor: 'pointer',
                                border: isSelected ? '2px solid #007bff' : '2px solid rgba(255,255,255,0.1)',
                                backgroundColor: isSelected ? 'rgba(0,123,255,0.1)' : 'transparent',
                                transition: 'all 0.2s'
                              }}>
                              <div style={{ fontWeight: '600', fontSize: '14px' }}>
                                {isRTL ? (eq.name_ar || eq.name) : eq.name}
                              </div>
                              <div style={{ color: '#9ca3af', fontSize: '12px', marginTop: '4px' }}>
                                {eq.serial_number || ''} {eq.model ? `• ${eq.model}` : ''}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Section: Checklist */}
                <div style={sectionSt}>
                  <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '600', color: '#60a5fa' }}>
                    {f.checklist || 'Checklist'}
                  </h3>
                  {(formData.checklist || []).map((item, idx) => (
                    <div key={idx} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '8px 12px', marginBottom: '8px',
                      backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '8px'
                    }}>
                      <span style={{ flex: 1, fontSize: '14px', color: '#d1d5db' }}>{item.text || item.item || item}</span>
                      <button type="button" onClick={() => removeChecklistItem(idx)}
                        style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '18px' }}>
                        ×
                      </button>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                    <input type="text" value={newChecklistItem}
                      onChange={(e) => setNewChecklistItem(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addChecklistItem(); } }}
                      placeholder={f.add_checklist_item || 'Add checklist item...'}
                      style={{ ...inputSt, flex: 1 }} />
                    <button type="button" onClick={addChecklistItem}
                      style={{ ...btnSecondary, whiteSpace: 'nowrap' }}>
                      + {f.add || 'Add'}
                    </button>
                  </div>
                </div>

                {/* Section: Instructions */}
                <div style={sectionSt}>
                  <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '600', color: '#60a5fa' }}>
                    {f.instructions || 'Instructions'}
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={labelSt}>{f.instructions_en || 'Instructions (EN)'}</label>
                      <textarea value={formData.instructions}
                        onChange={(e) => handleChange('instructions', e.target.value)}
                        style={{ ...inputSt, minHeight: '100px', resize: 'vertical' }}
                        rows={4} />
                    </div>
                    <div>
                      <label style={labelSt}>{f.instructions_ar || 'Instructions (AR)'}</label>
                      <textarea value={formData.instructions_ar}
                        onChange={(e) => handleChange('instructions_ar', e.target.value)}
                        style={{ ...inputSt, minHeight: '100px', resize: 'vertical', direction: 'rtl' }}
                        rows={4} />
                    </div>
                  </div>
                </div>

                {/* Submit Buttons */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                  <button type="button" onClick={() => setShowModal(false)} style={btnSecondary}>
                    {f.cancel || 'Cancel'}
                  </button>
                  <button type="submit" disabled={saving}
                    style={{
                      ...btnPrimary,
                      backgroundColor: saving ? '#6c757d' : '#007bff',
                      cursor: saving ? 'not-allowed' : 'pointer'
                    }}>
                    {saving
                      ? (f.saving || 'Saving...')
                      : editingPlan
                        ? (f.update_plan || 'Update Plan')
                        : (f.create_plan || 'Create Plan')}
                  </button>
                </div>

              </form>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default MaintenancePlans;
